import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';

import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import { makeStyles } from '@material-ui/core/styles';

import Table from '@base/Table';
import Toast from '@base/Toast';
import Title from '@base/Title';
import Filters from '@base/Filter';
import TextField from '@base/Inputs/Text';
import Boolean from '@base/Inputs/Boolean';
import Select from '@base/Inputs/Select';

import {
	verifyPagingAndSorting,
	PAGE,
	SIZE,
	DESC,
	TOTAL_ELEMENTS,
	TOTAL_PAGES,
} from '@base/Table/Utils';

import ObjetoPostalService from '@services/ObjetoPostalService';

import { removePagingAndSorting } from '@helpers/PaginationHelper';
import { ProcessoResolver } from '@helpers/ProcessoHelper';
import LabelIntruso from '@base/LabelIntruso';
import LabelRecontagem from '@base/LabelRecontagem';
import SearchIcon from '@material-ui/icons/Search'
import RefreshIcon from '@material-ui/icons/Refresh'

import DateFnsUtils from '@date-io/date-fns';

import { toDate } from '@helpers/DateHelper';

import brLocale from 'date-fns/locale/pt-BR';

import AplicarLegenda from '@base/AplicarLegenda';

import DialogConfirm from "../../base/Dialog/DialogConfirm";

const useStyles = makeStyles(
	(theme) => (
		{
			wrapper: {
				padding: theme.spacing(2)
			},
			title: {
				color: "#333",
				fontSize: 24,
			},
			formControl: {
				margin: theme.spacing(2, 0),
				width: '100%',
			}
		}
	)
);

const localeMap = {
	br: brLocale,
};

function useQuery() {
	const { search } = useLocation();
	return useMemo(() => new URLSearchParams(search), [search]);
}

export const ObjetoPostal = () => {

	const [loading, setLoading] = useState(true);
	const [list, setList] = useState([]);
	const [page, setPage] = useState(PAGE);
	const [size, setSize] = useState(SIZE);
	const [defaultOrderBy,] = useState('edicaoAuditInfo.dataUltimaAlteracao');
	const [sort, setSort] = useState([{ orderBy: defaultOrderBy, order: DESC }])
	const [totalElements, setTotalElements] = useState(TOTAL_ELEMENTS);
	const [totalPages, setTotalPages] = useState(TOTAL_PAGES);
	const [focoBotaoFiltrar, ] = useState(true);
	const [filtros, setFiltros] = useState([]);
	const [filtroAtivo, setFiltroAtivo] = useState(false);
	const [filtroNumeroObjetoPostal, setFiltroNumeroObjetoPostal] = useState('');
	const [filtroDataPostagem, setFiltroDataPostagem] = useState('');
	const [filtroDataRecebimento, setFiltroDataRecebimento] = useState('');
	const [filtroDataTriagem, setFiltroDataTriagem] = useState('');
	const [filtroDataContagem, setFiltroDataContagem] = useState('');
	const [filtroDataArmazenamento, setFiltroDataArmazenamento] = useState('');
	const [filtroDataDigitalizacao, setFiltroDataDigitalizacao] = useState('');
	const [filtroStatus, setFiltroStatus] = useState('');
	const [filtroInep, setFiltroInep] = useState('');
	const [filtroCodigoCartao, setFiltroCodigoCartao] = useState('');
	const [filtroVazios, setFiltroVazios] = useState('');
	const [filtroComMaisDeUmaEscola, setFiltroComMaisDeUmaEscola] = useState('');
	const [filtroComPendencias, setFiltroComPendencias] = useState('');
	const [filtroOrigem, setFiltroOrigem] = useState('');
	const [filtroCodigoCaixa, setFiltroCodigoCaixa] = useState('');
	const [quantidadeFiltrosAplicados, setQuantidadeFiltrosAplicados] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [objetoEscolhido, ] = useState();

	const pageName = 'Objetos Postais';
	const params = useParams();
	const history = useHistory();
	const processo = ProcessoResolver(params.processo);
	const toastRef = useRef(null);
	const query = useQuery();

	const classes = useStyles();

	const aplicarFiltros = (ativo = false) => {

		let f = [];

		if (filtroDataPostagem) f.push({ field: 'dataPostagem', value: toDate(filtroDataPostagem) });
		if (filtroNumeroObjetoPostal) f.push({ field: 'numeroObjetoPostal', value: filtroNumeroObjetoPostal });
		if (filtroDataRecebimento) f.push({ field: 'dataRecebimento', value: toDate(filtroDataRecebimento) });
		if (filtroDataTriagem) f.push({ field: 'dataTriagem', value: toDate(filtroDataTriagem) });
		if (filtroDataContagem) f.push({ field: 'dataContagem', value: toDate(filtroDataContagem) });
		if (filtroDataArmazenamento) f.push({ field: 'dataArmazenamento', value: toDate(filtroDataArmazenamento) });
		if (filtroDataDigitalizacao) f.push({ field: 'dataDigitalizacao', value: toDate(filtroDataDigitalizacao) });
		if (filtroStatus) f.push({ field: 'status', value: filtroStatus });
		if (filtroInep) f.push({ field: 'inep', value: filtroInep });
		if (filtroCodigoCartao) f.push({ field: 'codigoCartao', value: filtroCodigoCartao });
		if (filtroVazios) f.push({ field: 'vazios', value: filtroVazios });
		if (filtroComMaisDeUmaEscola) f.push({ field: 'comMaisDeUmaEscola', value: filtroComMaisDeUmaEscola });
		if (filtroComPendencias) f.push({ field: 'comPendencias', value: filtroComPendencias });
		if (filtroOrigem) f.push({ field: 'origem', value: filtroOrigem });
		if (filtroCodigoCaixa) f.push({ field: 'codigoCaixa', value: filtroCodigoCaixa });

		setFiltros(f);
		setFiltroAtivo(ativo);
		setQuantidadeFiltrosAplicados(f.length);

	}

	const contarFiltrosAplicados = () => quantidadeFiltrosAplicados;

	const limparFiltros = () => {

		setSort([{ orderBy: defaultOrderBy, order: DESC }]);
		setPage(PAGE);
		setSize(SIZE);
		setFiltros([]);
		setFiltroAtivo(false);
		setFiltroDataPostagem(null);
		setFiltroNumeroObjetoPostal('');
		setFiltroDataRecebimento(null);
		setFiltroDataTriagem(null);
		setFiltroDataContagem(null);
		setFiltroDataArmazenamento(null);
		setFiltroDataDigitalizacao(null);
		setFiltroStatus('');
		setFiltroInep('');
		setFiltroCodigoCartao('');
		setFiltroVazios('');
		setFiltroComMaisDeUmaEscola('');
		setFiltroComPendencias('');
		setFiltroOrigem('');
		setFiltroCodigoCaixa('');
		setQuantidadeFiltrosAplicados(0);

	}

	const columns = [
		{ label: 'Objeto', name: 'codigoPostal', filter: 'numeroObjetoPostal',
			func: (c,l) =>
				<>
					<span style={{cursor: 'pointer', textDecoration: 'underline'}}
                        onClick={() => redirecionarParaPaginaDeConteudo(c)}>{c}
					</span> 
					{l.possuiEscolasIntrusas === true && <LabelIntruso label="Escolas Intrusas"/>}
					{l.numeroRecontagens > 0 && <LabelRecontagem label={`Recontagens: ${l.numeroRecontagens}`}/>}
				</> , key: false },
		{ label: 'Caixa', name: 'caixa', func: (c,l) => l.origem === "ENVELOPE" ? c : "--", key: false },
    	{ label: 'Origem', name: 'origem', func: (c,l) => formatarPalavra(c), key: false },
		{ label: 'Recebimento', name: 'dataRecebimento', func: (objColuna,objLinha) => renderizaDataEResponsavel(objLinha.dataRecebimento,objLinha.responsavelPortaria) , key: false },
    	{ label: 'Triagem', name: 'dataTriagem', func: (objColuna,objLinha) => renderizaDataEResponsavel(objLinha.dataTriagem,objLinha.responsavelTriagem) , key: false },
    	{ label: 'Lançamento', name: 'dataContagem', func: (objColuna,objLinha) => renderizaDataEResponsavel(objLinha.dataContagem,objLinha.responsavelContagem) , key: false },
		{ label: 'Digitalização', name: 'dataDigitalizacao',func: (objColuna,objLinha) => renderizaDataEResponsavel(objLinha.dataDigitalizacao,objLinha.responsavelDigitalizacao), key: false },
    	{ label: 'Armazenamento', name: 'dataArmazenamento', func: (objColuna,objLinha) => renderizaDataEResponsavel(objLinha.dataArmazenamento,objLinha.responsavelArmazenamento) , key: false },
    	{ label: 'Digitalizações', name: 'qtdCartoesRecebidos', func: (c,l) => <AplicarLegenda n1={l.cartoesN1Recebidos} n2={l.cartoesN2Recebidos} n3={l.cartoesN3Recebidos} /> , key: false, ordering: false },
		{ label: 'Status', name: 'status', func: (c,l) => formatarPalavra(l.status), key: false }
	];

	const actions = [
		{
			name: () => 'Atualizar Status',
			func: (id, obj) => atualizarStatus(obj.id, false),
			icon: <RefreshIcon />,
			disabled: (id,obj) => obj.status !== "PROCESSANDO"
		},
		{
			name: () => 'Visualizar Conteúdo',
			func: (id, obj) => redirecionarParaPaginaDeConteudo(obj.codigoPostal),
			icon: <SearchIcon />,
			disabled: () => false
		},
	];

	function formatarPalavra(palavra) {
		if(palavra === "LANCADO") return "Lançado";

		return palavra.charAt(0).toUpperCase() + palavra.slice(1).toLowerCase();
	}

 /* function dialogObjetosZerados(idObjetoPostal) {
    setObjetoEscolhido(idObjetoPostal)
    setOpenDialog(true);
  }*/

  function renderizaDataEResponsavel(data,responsavel){

	if(data){
		return <>
				{data !== null ? data.substring(0,5) : "--"}
				{responsavel !== "--" ? <span style={{display:"block", color:"#999", fontSize:11}}>{responsavel.split(" ")[0]}</span>: ""}
			</>
	}else{
		return "";
	}

  }

	function redirecionarParaPaginaDeConteudo(idObjetoPostal) {
		history.push(`/${processo.id}/objetosPostais/${idObjetoPostal}`);
	}

	function atualizarStatus(idObjetoPostal, vazio) {
    	ObjetoPostalService.finalizar(processo.id, idObjetoPostal).then(resultado => {
			toastRef.current.show("success", "Objeto postal atualizado.");
			carregarDados();
     		setOpenDialog(false);
		});
	}

	const carregarDados = useCallback(() => {
		const f = verifyPagingAndSorting(removePagingAndSorting(filtros), page, size, sort, filtroAtivo);

		if (query.get('caixa') && f.filter(filtro => filtro.field === 'codigoCaixa' && filtro.value).length === 0) {
			f.push({ field: 'codigoCaixa', value: query.get('caixa') });
			setFiltroCodigoCaixa(query.get('caixa'));
		}

		ObjetoPostalService.filtrar(processo.id, f)
			.then(resultado => {
				const { content, number, size, totalElements, totalPages } = resultado.data;

				setList(content);
				setPage(number);
				setSize(size);
				setTotalElements(totalElements);
				setTotalPages(totalPages);
				setLoading(false);

			}).catch(error => toastRef.current.show("error", "Ocorreu um Problema ao Realizar Filtros!"));

	}, [filtros, filtroAtivo, page, size, sort, query, processo.id]);

	const handlePage = page => { setPage(page); setFiltroAtivo(true); };
	const handleRowsPerPage = (size, page) => { setPage(page); setSize(size); setFiltroAtivo(true); };
	const verifyOrderBy = (orderBy) => orderBy === 'seEnviaEmail' ? 'tipoPendenciaConfiguracaoEmail.enviaEmailParaEscola' :
											orderBy === 'codigoObjetoPostal' ? 'numeroObjetoPostal' :
												orderBy === 'idCaixa' ? 'caixa.codigo' : orderBy;

	const handleSort = (orderBy, order) => {
		if (sort.find(s => s.orderBy === verifyOrderBy(orderBy))) {
			let newSort = sort.filter(s => s.orderBy !== verifyOrderBy(orderBy));
			newSort.splice(sort.indexOf(sort.find(s => s.orderBy === verifyOrderBy(orderBy))), 0, { orderBy: verifyOrderBy(orderBy), order: order })
			setSort([...newSort]);
		} else {
			setSort([...sort.filter(s => s.orderBy !== verifyOrderBy(orderBy)), { orderBy: verifyOrderBy(orderBy), order: order }]);
		}

		setFiltroAtivo(true);
	}

	const handleClearSort = (orderBy) => { setFiltroAtivo(true); orderBy ? setSort([...sort.filter(s => s.orderBy !== verifyOrderBy(orderBy))]) : setSort([{ orderBy: defaultOrderBy, order: DESC }]) };
	const handleTotalPages = () => totalPages;
	const handleTotalElements = () => totalElements;

	useEffect(() => carregarDados(), [carregarDados]);

	return (
		<>
			<Toast ref={toastRef} />

			<Title>{pageName}</Title>

			<Filters
				handleFilter={aplicarFiltros}
				handleFilterClear={limparFiltros}
				countFilters={contarFiltrosAplicados}
				hasFocusButtonFilter={focoBotaoFiltrar}
			>

				<Grid item container spacing={3}>
					<Grid item sm={12} lg={3}>
						<TextField
							label='Numero Objeto Postal'
							value={filtroNumeroObjetoPostal}
							name='filtroNumeroObjetoPostal'
							onChange={(e) => setFiltroNumeroObjetoPostal(e.target.value)}
						/>
					</Grid>
				</Grid>

				<Grid item container spacing={3}>
					<Grid item sm={12} lg={2}>
						<FormControl className={classes.formControl}>
							<MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeMap["br"]}>
								<KeyboardDatePicker
									ampm={false}
									showTodayButton
									cancelLabel="Cancelar"
									okLabel="Salvar"
									todayLabel="Agora"
									format="dd/MM/yyyy"
									KeyboardButtonProps={{ 'aria-label': 'Selecione o Data', 'color': 'primary' }}
									label='Data de Postagem'
									value={filtroDataPostagem}
									onChange={(e) => setFiltroDataPostagem(e)}
									allowKeyboardControl
									invalidDateMessage={filtroDataPostagem ? "Formato de Data Inválido" : ""}
									invalidLabel={filtroDataPostagem ? "Data Inválida" : ""}
									mask="__/__/____"
									views={['date']}
									disabled={false}
								/>
							</MuiPickersUtilsProvider>
						</FormControl>
					</Grid>
					<Grid item sm={12} lg={2}>
						<FormControl className={classes.formControl}>
							<MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeMap["br"]}>
								<KeyboardDatePicker
									ampm={false}
									showTodayButton
									cancelLabel="Cancelar"
									okLabel="Salvar"
									todayLabel="Agora"
									format="dd/MM/yyyy"
									KeyboardButtonProps={{ 'aria-label': 'Selecione o Data', 'color': 'primary' }}
									label='Data de Recebimento'
									value={filtroDataRecebimento}
									onChange={(e) => setFiltroDataRecebimento(e)}
									allowKeyboardControl
									invalidDateMessage={filtroDataRecebimento ? "Formato de Data Inválido" : ""}
									invalidLabel={filtroDataRecebimento ? "Data Inválida" : ""}
									mask="__/__/____"
									views={['date']}
									disabled={false}
								/>
							</MuiPickersUtilsProvider>
						</FormControl>
					</Grid>
					<Grid item sm={12} lg={2}>
						<FormControl className={classes.formControl}>
							<MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeMap["br"]}>
								<KeyboardDatePicker
									ampm={false}
									showTodayButton
									cancelLabel="Cancelar"
									okLabel="Salvar"
									todayLabel="Agora"
									format="dd/MM/yyyy"
									KeyboardButtonProps={{ 'aria-label': 'Selecione a Data', 'color': 'primary' }}
									label="Data de Triagem"
									value={filtroDataTriagem}
									onChange={(e) => setFiltroDataTriagem(e)}
									allowKeyboardControl
									invalidDateMessage={filtroDataTriagem ? "Formato de Data Inválido" : ""}
									invalidLabel={filtroDataTriagem ? "Data Inválida" : ""}
									mask="__/__/____"
									views={['date']}
									disabled={false}
								/>
							</MuiPickersUtilsProvider>
						</FormControl>
					</Grid>
					<Grid item sm={12} lg={2}>
						<FormControl className={classes.formControl}>
							<MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeMap["br"]}>
								<KeyboardDatePicker
									ampm={false}
									showTodayButton
									cancelLabel="Cancelar"
									okLabel="Salvar"
									todayLabel="Agora"
									format="dd/MM/yyyy"
									KeyboardButtonProps={{ 'aria-label': 'Selecione a Data', 'color': 'primary' }}
									label="Data de Lançamento"
									value={filtroDataContagem}
									onChange={(e) => setFiltroDataContagem(e)}
									allowKeyboardControl
									invalidDateMessage={filtroDataContagem ? "Formato de Data Inválido" : ""}
									invalidLabel={filtroDataContagem ? "Data Inválida" : ""}
									mask="__/__/____"
									views={['date']}
									disabled={false}
								/>
							</MuiPickersUtilsProvider>
						</FormControl>
					</Grid>
					<Grid item sm={12} lg={2}>
						<FormControl className={classes.formControl}>
							<MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeMap["br"]}>
								<KeyboardDatePicker
									ampm={false}
									showTodayButton
									cancelLabel="Cancelar"
									okLabel="Salvar"
									todayLabel="Agora"
									format="dd/MM/yyyy"
									KeyboardButtonProps={{ 'aria-label': 'Selecione a Data', 'color': 'primary' }}
									label="Data de Digitalização"
									value={filtroDataDigitalizacao}
									onChange={(e) => setFiltroDataDigitalizacao(e)}
									allowKeyboardControl
									invalidDateMessage={filtroDataDigitalizacao ? "Formato de Data Inválido" : ""}
									invalidLabel={filtroDataDigitalizacao ? "Data Inválida" : ""}
									mask="__/__/____"
									views={['date']}
									disabled={false}
								/>
							</MuiPickersUtilsProvider>
						</FormControl>
					</Grid>
					<Grid item sm={12} lg={2}>
						<FormControl className={classes.formControl}>
							<MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeMap["br"]}>
								<KeyboardDatePicker
									ampm={false}
									showTodayButton
									cancelLabel="Cancelar"
									okLabel="Salvar"
									todayLabel="Agora"
									format="dd/MM/yyyy"
									KeyboardButtonProps={{ 'aria-label': 'Selecione a Data', 'color': 'primary' }}
									label="Data de Armazenamento"
									value={filtroDataArmazenamento}
									onChange={(e) => setFiltroDataArmazenamento(e)}
									allowKeyboardControl
									invalidDateMessage={filtroDataArmazenamento ? "Formato de Data Inválido" : ""}
									invalidLabel={filtroDataArmazenamento ? "Data Inválida" : ""}
									mask="__/__/____"
									views={['date']}
									disabled={false}
								/>
							</MuiPickersUtilsProvider>
						</FormControl>
					</Grid>
				</Grid>

				<Grid item container spacing={3}>
					<Grid item sm={12} lg={3}>
						<Select
							label='Status'
							value={filtroStatus}
							options={[
								{ label: 'Recebido', value: 'RECEBIDO' },
								{ label: 'Triado', value: 'TRIADO' },
								{ label: 'Lançado', value: 'LANCADO' },
								{ label: 'Em Recontagem', value: 'EM_RECONTAGEM' },
								{ label: 'Finalizado', value: 'FINALIZADO' }
							]}
							name='filtroStatus'
							onChange={(e) => setFiltroStatus(e.target.value)}
						/>
					</Grid>
					<Grid item sm={12} lg={3}>
						<TextField
							label='INEP'
							value={filtroInep}
							name='filtroInep'
							onChange={(e) => setFiltroInep(e.target.value)}
						/>
					</Grid>
					<Grid item sm={12} lg={3}>
						<TextField
							label='Código de Cartão'
							value={filtroCodigoCartao}
							name='filtroCodigoCartao'
							onChange={(e) => setFiltroCodigoCartao(e.target.value)}
						/>
					</Grid>
					<Grid item sm={12} lg={3}>
						<Select
							label={'Origem'}
							value={filtroOrigem}
							options={[
								{ value: "ENVELOPE", label: "Envelope" },
								{ value: "APLICATIVO", label: "Aplicativo" },
								{ value: "IMPA", label: "IMPA" }
							]}
							name='filtroOrigem'
							onChange={(e) => setFiltroOrigem(e.target.value)}
						/>
					</Grid>
				</Grid>

				<Grid item container spacing={3}>
					<Grid item sm={12} lg={3}>
						<Boolean
							label={'Vazios?'}
							value={filtroVazios}
							name='filtroVazios'
							onChange={(e) => setFiltroVazios(e.target.value)}
						/>
					</Grid>
					<Grid item sm={12} lg={3}>
						<Boolean
							label={'Com mais de uma escola?'}
							value={filtroComMaisDeUmaEscola}
							name='filtroComMaisDeUmaEscola'
							onChange={(e) => setFiltroComMaisDeUmaEscola(e.target.value)}
						/>
					</Grid>
					<Grid item sm={12} lg={3}>
						<Boolean
							label={'Com pendências?'}
							value={filtroComPendencias}
							name='filtroComPendencias'
							onChange={(e) => setFiltroComPendencias(e.target.value)}
						/>
					</Grid>
					<Grid item sm={12} lg={2}>
						<TextField
							label='Código da Caixa'
							value={filtroCodigoCaixa}
							name='filtroCodigoCaixa'
							onChange={(e) => setFiltroCodigoCaixa(e.target.value)}
						/>
					</Grid>
				</Grid>
			</Filters>

			<Grid item container spacing={3} style={{ padding: '32px 8px 0px', marginBottom: '0px'}}>
				<Grid item sm={12} lg={3}>
					Legenda: <AplicarLegenda n1="Cartões N1" n2="Cartões N2" n3="Cartões N3" />
				</Grid>
			</Grid>

			<Table
				columns={columns}
				tableData={list}
				actions={actions}
				page={page}
				handlePage={handlePage}
				handleTotalPages={handleTotalPages}
				handleTotalElements={handleTotalElements}
				rowsPerPage={size}
				handleRowsPerPage={handleRowsPerPage}
				sort={sort}
				handleSort={handleSort}
				handleClearSort={handleClearSort}
				loading={loading}
			/>
      {openDialog &&
        <DialogConfirm
          maxWidth={"sm"}
          id={{id: objetoEscolhido}}
          text={"Deseja realmente FINALIZAR o objeto postal manualmente? Esta ação requer cuidado e só deve ser utilizada quando nas seguintes situações: Objeto sem cartões ou Objeto com mais de uma escola (Escolas Intrusas)"}
          close={() => setOpenDialog(false)}
          open={openDialog}
          title={"Finalização Manual"}
          handleConfirm={() => atualizarStatus(objetoEscolhido, true)}
        />
      }
      		</>
	);

}

export default ObjetoPostal;

